"use client";

import { useEffect } from "react";
import Link from "next/link";
import { reportCaughtException } from "@/lib/sentry-lazy";
import { describeError } from "@/lib/errorSummary";

const bodyStyle = {
  margin: 0,
  minHeight: "100dvh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "#faf5ff",
  color: "#0f172a",
  fontFamily:
    '"Apple SD Gothic Neo", "Pretendard", system-ui, -apple-system, sans-serif',
} as const;

const mainStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: 20,
  textAlign: "center",
  maxWidth: 360,
} as const;

const titleStyle = { fontSize: 24, margin: 0, letterSpacing: "-0.01em" };
const summaryStyle = { fontSize: 14, lineHeight: 1.6, color: "#475569", margin: 0 };
const bodyTextStyle = { fontSize: 12, lineHeight: 1.5, color: "#64748b", margin: 0 };
const digestStyle = { fontSize: 12, color: "#94a3b8", margin: 0 };
const monoStyle = { fontFamily: "monospace" };
const actionsStyle = {
  display: "flex",
  gap: 8,
  flexWrap: "wrap",
  justifyContent: "center",
} as const;
const resetButtonStyle = {
  padding: "12px 20px",
  borderRadius: 16,
  border: "none",
  cursor: "pointer",
  background: "linear-gradient(135deg, #a855f7, #ec4899)",
  color: "white",
  fontSize: 15,
  fontWeight: 600,
} as const;
const homeLinkStyle = {
  padding: "12px 20px",
  borderRadius: 16,
  border: "1px solid #e9d5ff",
  background: "rgba(255,255,255,0.8)",
  color: "#7e22ce",
  fontSize: 15,
  textDecoration: "none",
  fontWeight: 600,
} as const;

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // SDK 준비 전이면 버퍼링 후 즉시 부팅 — 초기 렌더 크래시도 유실되지 않는다.
    reportCaughtException(error);
    console.error(error);
  }, [error]);

  const summary = describeError(error);

  return (
    <html lang="ko">
      <body style={bodyStyle}>
        <main style={mainStyle}>
          <h1 style={titleStyle}>
            문제가 발생했어요
          </h1>
          <p style={summaryStyle}>
            {summary}
          </p>
          <p style={bodyTextStyle}>
            페이지를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.
          </p>
          {error.digest ? (
            <p style={digestStyle}>
              오류 코드: <span style={monoStyle}>{error.digest}</span>
            </p>
          ) : null}
          <div style={actionsStyle}>
            <button
              type="button"
              onClick={reset}
              style={resetButtonStyle}
            >
              다시 시도
            </button>
            <Link
              href="/"
              style={homeLinkStyle}
            >
              홈으로
            </Link>
          </div>
        </main>
      </body>
    </html>
  );
}
